/**
 * Domain → Subdomain scope header — the reusable scope picker for per-site WAF
 * views. Fetches DNS zones (site picker) and a zone's records (subdomain picker,
 * filtered to `ever_proxied`), persists the selection in localStorage, and
 * reports it up via `onChange`.
 *
 * - `mode="zone"`      → DNS pages: zone picker only; the subdomain slot is a
 *   fixed, disabled "All site" chip (the `[zone] → [All site]` look).
 * - `mode="subdomain"` → DEM pages: zone picker + subdomain picker. Emits the
 *   chosen FQDN; emits `undefined` when the zone has no `ever_proxied` record.
 *
 * Optional props tailor it for non-analytics callers without changing the
 * defaults the analytics pages rely on:
 * - `allowAllSites`      → whether the site picker offers "All site" (org-wide).
 *   Defaults to true in `zone` mode. Set false to force a concrete zone (the DNS
 *   Records page, which manages one zone's records).
 * - `allowAllSubdomains` → in `subdomain` mode, add an "All domain" option that
 *   emits `{ zone, allDomains: true }` (the Overview dashboard).
 * - `defaultAllSites`    → default the initial selection to "All site" rather
 *   than the first zone (the Overview dashboard).
 * - `storeKey`           → override the localStorage key so a page keeps its own
 *   persisted scope instead of sharing the analytics one.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Badge,
  Skeleton,
} from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import {
  useWafDnsZones,
  useWafDnsZoneDomains,
  type WafDnsZone,
} from '@/api/waf';
import { loadJSON, saveJSON } from './storage';
import styles from './WafAnalytics.module.css';

/** Sentinel zone value for the org-wide "All site" option. */
const ALL_SITES = '__all__';
/** Sentinel subdomain value for the "All domain" option (subdomain mode). */
const ALL_DOMAINS = '__all_domains__';

export interface WafScope {
  zone?: WafDnsZone;
  subdomain?: string;
  /** True when "All site" is chosen — query org-wide (no site_id). */
  allSites?: boolean;
  /** True when a concrete site is chosen but "All domain" (no subdomain). */
  allDomains?: boolean;
}

interface StoredScope {
  zoneId?: string;
  subdomain?: string;
}

export function WafScopeBar({
  serviceId,
  mode,
  onChange,
  allowAllSites,
  allowAllSubdomains = false,
  defaultAllSites = false,
  storeKey: storeKeyProp,
  className,
}: {
  serviceId: string;
  mode: 'zone' | 'subdomain';
  onChange: (scope: WafScope) => void;
  allowAllSites?: boolean;
  allowAllSubdomains?: boolean;
  defaultAllSites?: boolean;
  storeKey?: string;
  /** Extra class on the root group — e.g. to widen the picker in a packed toolbar. */
  className?: string;
}) {
  const navigate = useNavigate();
  const storeKey = storeKeyProp ?? `waf:${serviceId}:analytics-scope`;
  const stored = useRef<StoredScope>(loadJSON<StoredScope>(storeKey, {}));

  // "All site" is offered in zone mode by default; callers opt in/out explicitly.
  const allowAll = allowAllSites ?? mode === 'zone';
  // "All domain" only applies to the subdomain picker.
  const allowAllSub = mode === 'subdomain' && allowAllSubdomains;

  const [zoneId, setZoneId] = useState(stored.current.zoneId ?? '');
  const [subdomain, setSubdomain] = useState(stored.current.subdomain ?? '');

  const zonesQ = useWafDnsZones(serviceId);
  const zones = useMemo(() => zonesQ.data?.items ?? [], [zonesQ.data]);
  // TODO: draft zones (zone_status === 0) aren't set up yet — disabled here;
  // they'll be completed via the setup-domain page later.
  const isDraftZone = (z: WafDnsZone) => z.zone_status === 0;
  const selectableZones = useMemo(() => zones.filter((z) => !isDraftZone(z)), [zones]);

  // Draft (unpublished) zones aren't a valid scope — picking one sends the user
  // to the add-site wizard to finish setting it up rather than selecting it.
  const onZoneChange = (v: string) => {
    const z = zones.find((zz) => zz.id === v);
    if (z && isDraftZone(z)) {
      // The zone already exists (just unpublished) — resume the wizard past the
      // eligibility/create step by handing it the zone id + domain.
      navigate(
        `/services/waf/${serviceId}/sites/new?zone_id=${encodeURIComponent(z.id)}&domain=${encodeURIComponent(z.domain)}`,
      );
      return;
    }
    setZoneId(v);
  };

  const domainsQ = useWafDnsZoneDomains(
    serviceId,
    mode === 'subdomain' && zoneId && zoneId !== ALL_SITES ? zoneId : undefined,
  );
  // Only records that have edge data are valid scopes (ever_proxied, NOT proxied).
  const subdomains = useMemo(
    () => (domainsQ.data?.items ?? []).filter((d) => d.ever_proxied),
    [domainsQ.data],
  );

  // Pick a valid, non-draft zone once zones arrive (stored one may be gone/draft).
  // When "All site" is allowed it's a valid selection; a dashboard can also open
  // on "All site" via `defaultAllSites`.
  useEffect(() => {
    if (allowAll && zoneId === ALL_SITES) return;
    if (!selectableZones.length) {
      if (allowAll) setZoneId(ALL_SITES);
      return;
    }
    if (!selectableZones.some((z) => z.id === zoneId)) {
      setZoneId(allowAll && defaultAllSites ? ALL_SITES : selectableZones[0].id);
    }
  }, [selectableZones, zoneId, allowAll, defaultAllSites]);

  // Pick a valid subdomain whenever the eligible set changes.
  useEffect(() => {
    if (mode !== 'subdomain') return;
    if (zoneId === ALL_SITES) return; // subdomain slot is fixed while org-wide
    if (domainsQ.isLoading) return;
    const valid =
      subdomains.some((d) => d.name === subdomain) || (allowAllSub && subdomain === ALL_DOMAINS);
    if (!valid) setSubdomain(allowAllSub ? ALL_DOMAINS : (subdomains[0]?.name ?? ''));
  }, [mode, subdomains, subdomain, domainsQ.isLoading, zoneId, allowAllSub]);

  // Emit + persist whenever the resolved selection changes.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    if (allowAll && zoneId === ALL_SITES) {
      saveJSON(storeKey, { zoneId, subdomain });
      onChangeRef.current({ allSites: true });
      return;
    }
    const zone = zones.find((z) => z.id === zoneId);
    if (!zone) return;
    saveJSON(storeKey, { zoneId, subdomain });
    if (mode === 'subdomain') {
      if (allowAllSub && subdomain === ALL_DOMAINS) {
        onChangeRef.current({ zone, allDomains: true });
      } else {
        onChangeRef.current({ zone, subdomain: subdomain || undefined });
      }
    } else {
      onChangeRef.current({ zone });
    }
  }, [zones, zoneId, subdomain, mode, allowAll, allowAllSub, storeKey]);

  if (zonesQ.isLoading) {
    return <Skeleton style={{ height: 38, maxWidth: 380 }} />;
  }
  if (zonesQ.isError) {
    return (
      <Alert tone="danger" title="Couldn't load domains">
        The DNS zones for this service couldn't be loaded.
      </Alert>
    );
  }
  if (!zones.length) {
    return (
      <Alert tone="info" title="No DNS zones">
        This service has no managed-DNS zones yet.
      </Alert>
    );
  }

  return (
    <div className={className ? `${styles.scopeGroup} ${className}` : styles.scopeGroup}>
      <Select value={zoneId} onValueChange={onZoneChange}>
        <SelectTrigger aria-label="Domain" className={styles.scopeSelect}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {allowAll && <SelectItem value={ALL_SITES}>All site</SelectItem>}
          {zones.map((z) => (
            // Draft zones stay selectable — choosing one navigates to setup — but
            // keep the muted "(Draft)" look they had while disabled.
            <SelectItem key={z.id} value={z.id}>
              {isDraftZone(z) ? (
                <span style={{ color: 'var(--vn-fg-subtle)' }}>{z.domain} (Draft)</span>
              ) : (
                z.domain
              )}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {mode === 'zone' ? (
        // A concrete zone is queried whole → show the fixed "All site" subdomain
        // slot (only when "All site" is a concept for this picker).
        allowAll &&
        zoneId !== ALL_SITES && (
          <>
            <ChevronRight size={16} className={styles.scopeArrow} aria-hidden />
            <Badge tone="neutral" appearance="subtle">All site</Badge>
          </>
        )
      ) : (
        <>
          <ChevronRight size={16} className={styles.scopeArrow} aria-hidden />
          {zoneId === ALL_SITES ? (
            <Badge tone="neutral" appearance="subtle">All domain</Badge>
          ) : domainsQ.isLoading ? (
            <Skeleton style={{ height: 38, width: 180 }} />
          ) : subdomains.length === 0 && !allowAllSub ? (
            <Badge tone="warning" appearance="subtle">No proxied subdomains</Badge>
          ) : (
            <Select value={subdomain || (allowAllSub ? ALL_DOMAINS : '')} onValueChange={setSubdomain}>
              <SelectTrigger aria-label="Subdomain" className={styles.scopeSelect}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {allowAllSub && <SelectItem value={ALL_DOMAINS}>All domain</SelectItem>}
                {subdomains.map((d) => (
                  <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </>
      )}
    </div>
  );
}

export default WafScopeBar;
